import { CONFIG } from '../config.js';
import { safeFetchClient } from '../utils/safe-fetch-client.js';
import { KIOSCO_ERROR_MESSAGES } from '../shared/kiosco-mensajes.js';
import { listReportes, contarReportesNuevos, marcarReporte } from '../infrastructure/reportes.repository.js';

/**
 * Reportes del staff (spec staff-reportes). El envío va por `/api/kiosco`
 * (PIN del empleado, mismos límites de intentos que el fichaje); la lista de
 * nombres sale del mismo `GET /api/kiosco` que muestra el kiosco, porque una
 * sesión `counter` no puede leer `empleados`. La lectura y las acciones del
 * admin van por `reportes.repository.js`.
 */

/** Tope de un reporte, en code points (el mismo conteo que `char_length` en SQL y que api/kiosco.js). */
export const REPORTE_MAX_CHARS = 2000;

const ENVIO_MESSAGES = {
    ...KIOSCO_ERROR_MESSAGES,
    texto_requerido: 'Escribí el mensaje.',
    texto_largo: `El mensaje es muy largo (hasta ${REPORTE_MAX_CHARS} caracteres).`,
};

const MARCAR_MESSAGES = {
    ya_leido: 'Ese mensaje ya estaba marcado como leído.',
    ya_archivado: 'Ese mensaje ya estaba archivado.',
    reporte_inexistente: 'Ese mensaje ya no existe.',
    solo_admin: 'Solo un admin puede hacer esto.',
    accion_invalida: 'Acción inválida.',
};

const GENERICO = KIOSCO_ERROR_MESSAGES.error_interno;

/** @param {unknown} err */
function reportarFalla(err) {
    window.Sentry?.captureException(err);
    // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
    console.error('reportes: no se pudo marcar el mensaje', err);
}

/** @param {string} texto @returns {number} largo en code points */
export function largoReporte(texto) {
    return [...String(texto ?? '').trim()].length;
}

/**
 * Chequeo del formulario antes de enviar (el servidor vuelve a validar todo).
 * @param {{ empleadoId?: string, pin?: string, texto?: string }} datos
 * @returns {{ ok: true } | { ok: false, mensaje: string }}
 */
export function validarReporte({ empleadoId, pin, texto } = {}) {
    const largo = largoReporte(texto);
    if (!empleadoId || !/^\d{4}$/.test(String(pin ?? '')) || largo === 0 || largo > REPORTE_MAX_CHARS) {
        return { ok: false, mensaje: `Elegí tu nombre, poné tu PIN y escribí el mensaje (hasta ${REPORTE_MAX_CHARS} caracteres).` };
    }
    return { ok: true };
}

/** @returns {Promise<Array<{ id: string, nombre: string }>>} empleados activos, como los muestra el kiosco */
export async function loadEmpleadosParaReporte() {
    const res = await safeFetchClient(CONFIG.API.KIOSCO, { method: 'GET' });
    if (!res.ok) throw new Error(`GET /api/kiosco ${res.status}`);
    const body = await res.json();
    return (body.empleados ?? []).map((e) => ({ id: e.id, nombre: e.nombre }));
}

/**
 * @param {{ empleadoId: string, pin: string, texto: string }} params
 * @returns {Promise<{ ok: true } | { ok: false, mensaje: string }>}
 */
export async function enviarReporte({ empleadoId, pin, texto }) {
    let res;
    try {
        res = await safeFetchClient(CONFIG.API.KIOSCO, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ tipo: 'reporte', empleado_id: empleadoId, pin: String(pin), texto: String(texto ?? '').trim() }),
        });
    } catch {
        // No answer (timeout, dropped connection): the message may have been
        // stored anyway, so say so instead of inviting a blind resend.
        return { ok: false, mensaje: 'Se cortó la conexión y no sabemos si el mensaje llegó. Avisale a Caro antes de mandarlo de nuevo.' };
    }
    const body = await res.json().catch(() => ({}));
    if (res.ok && body.ok) return { ok: true };
    return { ok: false, mensaje: ENVIO_MESSAGES[body.error] ?? GENERICO };
}

/** Admin: mensajes de un filtro (pass-through; una falla real se propaga). */
export function loadReportes(params) {
    return listReportes(params);
}

/** Admin: cantidad de Nuevos (lanza si la lectura falla). */
export function contarNuevos() {
    return contarReportesNuevos();
}

/**
 * Admin: marcar leído o archivar.
 * @param {{ reporteId: string, accion: 'leido'|'archivado' }} params
 * @returns {Promise<{ ok: true } | { ok: false, mensaje: string }>}
 */
export async function marcar(params) {
    try {
        const data = await marcarReporte(params);
        if (data?.ok) return { ok: true };
        if (!MARCAR_MESSAGES[data?.error]) reportarFalla(new Error(`marcar_reporte: respuesta inesperada ${JSON.stringify(data)}`));
        return { ok: false, mensaje: MARCAR_MESSAGES[data?.error] ?? GENERICO };
    } catch (err) {
        // Missing RPC (code deployed before migration 34), grants, network.
        reportarFalla(err);
        return { ok: false, mensaje: GENERICO };
    }
}
